import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react'
import { potencySuffix } from '@/lib/potency'
import { X, Plus, Minus, LayoutGrid, Package, Tag, MapPin, Percent, DollarSign, Clock, Check, Trash2, Search, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { ProductIcon, getCategoryIcon } from '@/lib/icons'
import { effectiveUnitPrice } from '@/lib/pricing'
import { pickFifoLot } from '@/lib/batches'
import { getProductTypeName, typeValueTextClass } from '@/lib/productType'
import { formatCurrency, extractTaxes } from '@/lib/utils'
import { withCdnImageWidth } from '@/lib/cdnImage'
import { usePOSStore, isReceiptPartiallyPaid } from '@/stores/posStore'
import { useCartStore } from '@/stores/cartStore'
import { useProductBatches } from '@/api/products'
import { usePOSConfig } from '@/api/config'
import { useDiscountReasons, useUpdateReceiptItem, useDeleteReceiptItem } from '@/api/receipts'
import {
  broadcastProductShown,
  broadcastProductHidden,
  toFeaturedProduct,
} from '@/lib/cartChannel'
import type { Product, ProductLot } from '@/types'
import { ManagerApprovalDialog } from '@/components/functions/ManagerApprovalDialog'
import {
  REMOVE_CART_DISCOUNT_FIRST,
  allApproved,
  approvalRows,
  approveDiscount,
  discardApprovals,
  cartSnapshot,
  probeDiscount,
  useDiscountLimit,
} from '@/hooks/useDiscountApproval'
import type { Cart } from '@/lib/cart'
import {
  approvalSubtitle,
  cartWithLineDiscounts,
  combinedDropExceedsLimit,
  isItemApproved,
  lineApprovalItem,
  toCents,
  type ApprovalItem,
  type LineDiscountTarget,
} from '@/lib/discountLimit'

interface ProductModalProps {
  product: Product
  onClose: () => void
}

type TabId = 'overview' | 'batch' | 'discount' | 'nearby'

interface TabDef {
  id: TabId
  label: string
  icon: typeof LayoutGrid
  show: boolean
  disabled?: boolean
  badge?: string | number
}

// Sentinel key for the "no batches" case (non-cannabis or empty batch list).
const NO_BATCH_KEY = '__no_batch__'

// `useProductBatches` synthesizes `__nolot_N__` when the BE returns an
// empty/duplicate batch_number, so every row gets a unique state key.
// The modal hides the marker in display and converts it back to `null`
// when writing the line to the cart so the receipt doesn't carry the
// synthetic string.
const isSyntheticLot = (key: string | null | undefined): boolean =>
  typeof key === 'string' && key.startsWith('__nolot_')
const lotKeyToCartLot = (key: string | null | undefined): string | null =>
  typeof key === 'string' && key !== NO_BATCH_KEY && !isSyntheticLot(key) ? key : null

const QUICK_PERCENTS = [5, 10, 15, 20] as const
const QUICK_DOLLARS = [1, 5, 10, 20] as const

// Mocked "nearby competitor price" data. Anchored to the product's base
// price so the spread always includes a few cheaper stores worth matching.
// Swap for a real `/nearby` endpoint once available.
const NEARBY_PAGE_SIZE = 20

interface NearbyStore {
  id: string
  name: string
  distance: number
  price: number
  updatedAt: Date
}

const NEARBY_STORE_NAMES = [
  'Downtown Cannabis Co.',
  'Green Leaf Dispensary',
  'High Note Lounge',
  "Budtender's Haven",
  'Urban Canopy',
  'The Joint Co.',
  'Terpene Trading Post',
  'Purple Haze Emporium',
  'Seedling Supply',
  'Mellow Mind Market',
  'Kush Corner',
  'Hazy Horizons',
  'Blaze & Brew',
  'Indica Icon',
  'Sativa Stop',
  'Hybrid Hub',
  'The Green Room',
  'Cannabis Corner',
  'Happy Harvest',
  'Zen Garden Co.',
  'Canopy Club',
  'Leaflight',
  'Nugget Nook',
  'Flora & Flame',
  'Good Growth',
  'BC Bud Barn',
  'Maple Leaf Marijuana',
  'North Star Cannabis',
  'Pacific Pot Co.',
  'Evergreen Emporium',
]

function generateNearbyStores(basePrice: number): NearbyStore[] {
  const now = Date.now()
  return NEARBY_STORE_NAMES.map((name, i) => {
    const multiplier = 0.78 + i * 0.017 + (i % 3) * 0.008
    const price = Math.max(0.01, Math.round(basePrice * multiplier * 100) / 100)
    const distance = Math.round((0.3 + ((i * 0.41) % 14)) * 10) / 10
    const minutesAgo = (i * 73 + 15) % (60 * 24 * 14)
    return {
      id: `store-${i}`,
      name,
      distance,
      price,
      updatedAt: new Date(now - minutesAgo * 60_000),
    }
  })
}

function formatRelativeTime(date: Date): string {
  const diffMs = Date.now() - date.getTime()
  const mins = Math.floor(diffMs / 60_000)
  if (mins < 60) return `${Math.max(1, mins)}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })
}

// Format THC/CBD display (max 2 decimals)
function formatPercent(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '0'
  const num = typeof value === 'number' ? value : parseFloat(value)
  if (!Number.isFinite(num)) return '0'
  if (num % 1 === 0) return `${num}`
  return parseFloat(num.toFixed(2)).toString()
}

// Show the weight whenever the product has a positive `mj_grams`, regardless of
// `is_cannabis`. Prefer the human-readable `product_unit` (e.g. "3x0.5g") when
// available; otherwise fall back to `mj_grams` rounded to a whole number + "g".
function getDisplayWeight(product: Product): string {
  const raw = product.mj_grams
  const grams = typeof raw === 'number' ? raw : parseFloat(String(raw ?? ''))
  if (!Number.isFinite(grams) || grams <= 0) return ''
  if (product.product_unit) return product.product_unit
  return `${Math.round(grams)}g`
}

// Filter out the BE's "no value" sentinels — empty string, literal
// dash, "N/A", "none" — so an empty Strain field doesn't render a
// useless "Strain -" card. Mirrors the rule used elsewhere for the
// strain badge.
function isMeaningfulValue(value: string | null | undefined): value is string {
  if (typeof value !== 'string') return false
  const n = value.trim().toLowerCase()
  return n !== '' && n !== '-' && n !== 'n/a' && n !== 'none'
}

function getProductFields(product: Product) {
  const isCannabis = product.is_cannabis === true
  // The legacy /ht/api/objects/products/:id payload returns `category` as a
  // numeric id rather than a name, so prefer the explicit *_name field and
  // only fall back to `category` when it's actually a string.
  const categoryName =
    (typeof product.category_name === 'string' && product.category_name) ||
    (typeof product.category === 'string' && product.category) ||
    ''
  return {
    name: product.display_name || product.description || product.name || 'Unknown Product',
    brand: product.brand_name || product.vendor_name || product.brand || '',
    category: categoryName,
    // Same defensive coercion as `category` above — refund product
    // enrichment can resolve a Product whose `strain` is a numeric FK.
    strain:
      (typeof product.strain === 'string' && product.strain) ||
      (typeof product.strain_type === 'string' && product.strain_type) ||
      '',
    typeName: getProductTypeName(product),
    thc: isCannabis ? (product.test_thc ?? product.thc_percent ?? null) : null,
    unit: isCannabis ? (product.test_units ?? null) : null,
    cbd: isCannabis ? (product.test_cbd ?? product.cbd_percent ?? null) : null,
    size: getDisplayWeight(product),
    icon: getCategoryIcon(categoryName),
    // `product_info_text` is the human-written marketing paragraph;
    // `description` is usually just the product name repeated, so it's
    // a fallback only.
    description: product.product_info_text || product.description || '',
    image: product.image_url || product.thumbnail_url || product.image || null,
  }
}

export function ProductModal({ product, onClose }: ProductModalProps) {
  const [imgError, setImgError] = useState(false)
  const [activeTab, setActiveTab] = useState<TabId>('overview')

  // Per-batch quantity map. For non-batched products, we use a single key.
  const [batchQty, setBatchQty] = useState<Record<string, number>>({})
  const [selectedBatchKey, setSelectedBatchKey] = useState<string>(NO_BATCH_KEY)

  // Discount tab state
  const [discountMode, setDiscountMode] = useState<'percent' | 'amount'>('amount')
  const [discountInput, setDiscountInput] = useState('')
  const [reasonId, setReasonId] = useState<number | null>(null)

  const addToCart = usePOSStore((s) => s.addToCart)
  const updateCartItem = usePOSStore((s) => s.updateCartItem)
  const removeFromCart = usePOSStore((s) => s.removeFromCart)
  // `pos.products.verify_if_is_over_on_hand` SysConfig — when false, the
  // modal lets the cashier oversell (IOH may be stale, transfers
  // in-flight). Default true (cap at on-hand) when the flag is missing.
  const { data: posConfig } = usePOSConfig()
  const verifyOverOnHand = posConfig?.posVerifyIfIsOverOnHand !== false
  // Refund mode locks pricing — the cart line carries whatever discount
  // was applied on the source receipt and the cashier cannot edit it.
  // Drives the Discount-tab hide + discount-validation bypass below so
  // the modal doesn't surface "Select a discount reason on the Discount
  // tab to continue" against a discount the cashier never entered.
  const isRefundMode = usePOSStore((s) => s.isRefundMode)
  const refundReceiptId = usePOSStore((s) => s.currentReceiptId)
  const setServerTotals = usePOSStore((s) => s.setServerTotals)
  // Tree-wide refund-mutation flag (set by useRefundCartMutations from
  // useIsMutating over the shared receiptItemMutationKey). Local
  // updateReceiptItem/deleteReceiptItem `.isPending` only sees THIS
  // modal's mutation instances; the cart's +/-/X buttons fire through
  // a different hook instance, so a PUT in flight from the cart side
  // wouldn't show up in the local flags. Folding this in makes Update
  // Cart wait for any in-flight mutation, refund-mode or otherwise.
  const isRefundMutating = usePOSStore((s) => s.isRefundMutating)

  // Refund-mode receipt mutations. Lot swaps and qty changes need to
  // hit the BE so taxes/totals stay accurate (refund carts skip
  // /cart/preview because preview rejects negative qty). Response is a
  // fresh Receipt; we re-seed serverTotals from it.
  const updateReceiptItem = useUpdateReceiptItem()
  const deleteReceiptItem = useDeleteReceiptItem()

  // Synchronous re-entry latch for handleSubmit. The existing guards
  // at the top of handleSubmit (mutation.isPending, isRefundMutating)
  // are state-based and settle a render AFTER mutateAsync starts -- a
  // double-click on "Save" or an Enter+click combo fires both
  // handlers in the same tick, before any flag flips. A ref
  // read+write is synchronous and beats the click rate. Released in
  // the finally so a transient error doesn't permanently lock the
  // modal (cashier can retry the submission).
  const submitInFlightRef = useRef<boolean>(false)

  const { data: batches } = useProductBatches(product.id, product.is_cannabis ?? false)
  const hasBatches = !!(batches && batches.length > 0)
  const { data: reasons = [] } = useDiscountReasons()

  // /cart/preview is authoritative on which batch each cart line will
  // commit as. The BE returns it as `batch_number` on every priced
  // row (legacy responses used `lot_number`); `useV2PreviewBridge`
  // keeps `cartStore.preview.items` in sync. We use this below to
  // back-fill the modal's seed when the local cart line was added
  // before its lot got pinned (optimistic add path).
  const previewItemsForSeed = useCartStore((s) => s.preview?.items ?? null)
  const previewBatchNumber: string | null = useMemo(() => {
    if (!previewItemsForSeed) return null
    const candidates = previewItemsForSeed.filter((it) => it.product_id === product.id)
    if (candidates.length !== 1) return null
    const m = candidates[0] as {
      batch_number?: string | null
      lot_number?: string | null
    }
    const lot = m.batch_number ?? m.lot_number ?? null
    return lot && typeof lot === 'string' ? lot : null
  }, [previewItemsForSeed, product.id])

  // ---------- Are we editing an existing cart line? ----------
  // If `cartItemId` was passed, we resolve the live cart line so we can seed
  // qty/discount/notes and route the CTA to "Update Cart" instead of adding.
  const cartItemId = product.cartItemId ?? null
  const existingCartItem = usePOSStore((s) =>
    cartItemId ? s.cart.find((i) => i.id === cartItemId) ?? null : null
  )
  const isEditing = existingCartItem !== null

  // Refund-mode lookup: in refund mode every product on the grid corresponds
  // to a recalled cart line, but the grid click path doesn't pass
  // `cartItemId` (only the cart-sidebar click does). Without this fallback
  // the price/cap fixes below silently no-op when the cashier opens the
  // modal from the grid, and they see catalog regular_price + on_hand cap
  // instead of paid price + max_refund_qty cap.
  const refundCartItem = usePOSStore((s) =>
    isRefundMode ? (s.cart.find((i) => i.product_id === product.id) ?? null) : null,
  )
  const refundLine = existingCartItem ?? refundCartItem

  // Seed-lot hint only — used by the seed effect to land on the same
  // batch the cart row shows when the modal opens from the grid for a
  // SKU already in the cart. Doesn't change qty seeding.
  const anyCartLineForProduct = usePOSStore((s) =>
    s.cart.find((i) => i.product_id === product.id) ?? null,
  )

  // ---------- Seed batch quantities once batches resolve ----------
  // When editing, the existing cart line's qty seeds onto its lot (or NO_BATCH);
  // when adding fresh, the FIFO batch (oldest package_date with positive
  // on-hand) gets a default of 1. Negative-quantity batches are still
  // displayed in the picker (BE keeps them as a stock-discrepancy
  // signal), but never auto-selected. Refund-mode cart lines carry
  // negative qty (e.g. -1); seed in absolute terms so the modal works
  // in positive numbers internally — the submit handler re-applies the
  // sign when writing back to the cart.
  useEffect(() => {
    const seedQty = isEditing ? Math.abs(existingCartItem!.quantity) : 1
    const seedLot =
      existingCartItem?.lot_number
      || anyCartLineForProduct?.lot_number
      || previewBatchNumber
      || product.lot_number
      || null

    setBatchQty((prev) => {
      if (hasBatches && batches) {
        const seedKey = seedLot && batches.some((b) => b.lot_number === seedLot)
          ? seedLot
          : pickFifoLot(batches) ?? batches[0].lot_number
        const prevKeys = Object.keys(prev)
        const newKeys = batches.map((b) => b.lot_number)
        const sameSet =
          prevKeys.length === newKeys.length && prevKeys.every((k) => newKeys.includes(k))
        if (sameSet && prevKeys.length > 0) return prev
        const seeded: Record<string, number> = {}
        for (const b of batches) seeded[b.lot_number] = b.lot_number === seedKey ? seedQty : 0
        return seeded
      }
      if (Object.keys(prev).length === 0) return { [NO_BATCH_KEY]: seedQty }
      return prev
    })

    setSelectedBatchKey((prev) => {
      if (hasBatches && batches) {
        const seedKey = seedLot && batches.some((b) => b.lot_number === seedLot)
          ? seedLot
          : pickFifoLot(batches) ?? batches[0].lot_number
        if (!batches.some((b) => b.lot_number === prev)) return seedKey
        return prev
      }
      return NO_BATCH_KEY
    })
  }, [hasBatches, batches, product.lot_number, isEditing, existingCartItem, anyCartLineForProduct, previewBatchNumber])

  // ---------- Seed discount from the existing cart line, once ----------
  // Stored discount on a CartItem is the *line* discount, but the modal
  // works in per-unit terms — divide by qty to recover the per-unit amount.
  // Use Math.abs on both sides so refund-mode lines (qty=-2) don't yield
  // a negative per-unit value that the input then silently clamps to 0,
  // dropping the cashier's discount.
  //
  // Skip in refund mode: refundUnitPrice is already the post-discount
  // figure the customer paid (line_total / qty). Seeding the source
  // receipt's discount on top would double-subtract it, e.g. a $47.49
  // line with -$10 off would show as -$27.49 instead of -$37.49.
  const [didSeedDiscount, setDidSeedDiscount] = useState(false)
  useEffect(() => {
    if (didSeedDiscount || !existingCartItem || isRefundMode) return
    const lineDiscount = Math.abs(existingCartItem.discount_amount ?? 0)
    const qty = Math.abs(existingCartItem.quantity || 1) || 1
    if (lineDiscount > 0) {
      setDiscountMode('amount')
      setDiscountInput(String(+(lineDiscount / qty).toFixed(2)))
      // Try to recover the reason id from the notes prefix we wrote on apply.
      const note = existingCartItem.notes ?? ''
      const m = note.match(/^Discount:\s*(.+)$/)
      if (m) {
        const found = reasons.find((r) => r.name === m[1])
        if (found) setReasonId(found.id)
      }
    }
    setDidSeedDiscount(true)
  }, [didSeedDiscount, existingCartItem, reasons, isRefundMode])

  const totalQty = useMemo(
    () => Object.values(batchQty).reduce((sum, n) => sum + (n || 0), 0),
    [batchQty]
  )

  // ---------- Customer-Facing Display: feature this product ----------
  // While the modal is open, broadcast `product.shown` so the /customer
  // route renders this product on its right panel (mirrors the legacy
  // display_waio.html show-product flow). Re-fires whenever the selected
  // customer changes so the CFD's tier price stays in sync with the
  // cart's repriced lines.
  //
  // We deliberately do NOT publish product.hidden on cleanup. The
  // featured product is a "most-recently-touched" affordance — it should
  // persist after the modal closes (the cashier might have added,
  // cancelled but still wanted the customer to see it, or be about to
  // open another modal). The CFD clears the feature when the cart
  // itself clears (commit/cancel). Cart-add call sites also re-broadcast
  // so a + click on the grid replaces the featured product.
  //
  // The CFD's effective price is published from a useEffect lower in
  // the component (see "Publish to CFD" below) — it depends on the
  // modal's `price` variable, which the precedence cascade computes
  // further down (refund → batch → preview → catalog estimate).
  const selectedCustomer = usePOSStore((s) => s.selectedCustomer)
  const previewItems = useCartStore((s) => s.preview?.items ?? null)

  // On unmount, tell the CFD to revert to the welcome state. Without
  // this the right panel keeps showing the last product the cashier
  // looked at, even after they close the modal — confusing for the
  // next customer.
  useEffect(() => {
    return () => {
      broadcastProductHidden()
    }
  }, [])

  const p = getProductFields(product)
  // Per-unit price the modal does its qty math against. Precedence:
  //   1. The BE-resolved unit price from /cart/preview when this product
  //      is already in the cart. The preview is authoritative for the
  //      current customer + active schedules and is exactly what
  //      CartItemRow / the receipt show — using it keeps the modal's
  //      "Save $X" math in lockstep with the cart total below.
  //   2. Customer-aware `effectiveUnitPrice` as the optimistic fallback
  //      (used when adding fresh, before the first preview returns).
  //
  // Without (1) the modal silently disagreed with the cart whenever the
  // FE estimate differed from what the BE priced — e.g. a walk-in where
  // the FE picked the member-tier `price2` ($60.18) but the cart
  // correctly used the regular price ($74.22).
  // Per-unit price the cart line will actually commit at. The BE's
  // `priced.price` is the catalog/regular price (pre-discount); the
  // post-discount per-unit number is `subtotal / qty`. Using `price`
  // directly silently surfaces $19.03 on a line that the receipt
  // commits at $17.13. Prefer subtotal/qty; fall back to `price` only
  // when subtotal is missing (defensive — current BE always returns it).
  //
  // Cashier-discount carve-out: when the cart line carries a per-line
  // `discount_amount`, the BE has already deducted it from `subtotal`,
  // and the seed effect below pre-fills the same value into the modal's
  // discount input. The modal's price math is `price − unitDiscountAmount`,
  // so dividing post-discount subtotal by qty would have the BE-deducted
  // $10 baked into `price` AND the seed re-deducting it on top — a cart
  // line showing $31.52 with a $10 discount applied would render as
  // $31.52 strike → $21.52 main, applying the same $10 a second time.
  // Add the line-total discount back before dividing to recover the
  // pre-cashier-discount per-unit value the modal is meant to work in.
  const pricedFromCart: number | null = (() => {
    if (!previewItems) return null
    const match = previewItems.find((it) => it.product_id === product.id)
    if (!match) return null
    const m = match as { price?: string | number; subtotal?: string | number; qty?: string | number }
    const qty = parseFloat(String(m.qty ?? 1)) || 1
    const subtotal = parseFloat(String(m.subtotal ?? ''))
    if (Number.isFinite(subtotal) && qty > 0) {
      const lineDiscount = Math.abs(existingCartItem?.discount_amount ?? 0)
      const unit = (subtotal + lineDiscount) / qty
      if (Number.isFinite(unit) && unit > 0) return unit
    }
    const n = parseFloat(String(m.price ?? ''))
    return Number.isFinite(n) && n > 0 ? n : null
  })()
  // Refund mode: lock to the per-unit price the customer actually paid on
  // the source receipt (carried on the recalled cart line). Prefer
  // `line_total / qty` because line_total is post-discount, pre-tax —
  // exactly the figure the BE will refund. unit_price on a recalled item
  // is the *gross* catalog price (see comment in posStore.ts:352 + the
  // `netPrice * qty - discount` math at posStore.ts:364), so using it
  // would silently re-introduce the bug for any line that had a discount
  // applied on the source sale.
  const refundUnitPrice: number | null = (() => {
    if (!isRefundMode || !refundLine) return null
    const qty = Math.abs(refundLine.quantity) || 1
    const fromLine = Math.abs(Number(refundLine.line_total ?? 0)) / qty
    if (Number.isFinite(fromLine) && fromLine > 0) return fromLine
    const fromUnit = Number(refundLine.unit_price)
    return Number.isFinite(fromUnit) && fromUnit > 0 ? fromUnit : null
  })()
  // Per-batch customer-priced number from the batches endpoint. With
  // `customer_id` on the request the v2 wrapper resolves group_id +
  // price_field and the handler returns `discounted_price` per batch;
  // without a customer it returns just `price`. This is the price
  // that's authoritative for "what the cashier sees when they pick
  // this batch in the modal" — the BatchPanel row already shows it, so
  // the footer must agree.
  const activeBatch = batches?.find((b) => b.lot_number === selectedBatchKey) ?? null
  const batchUnitPrice: number | null = (() => {
    const dp = activeBatch?.discounted_price
    if (typeof dp === 'number' && Number.isFinite(dp) && dp > 0) return dp
    const p = activeBatch?.price
    if (typeof p === 'number' && Number.isFinite(p) && p > 0) return p
    return null
  })()
  // Precedence:
  //   1. Refund: locked to source-receipt unit price.
  //   2. Active batch's price — when the cashier has a batch selected,
  //      THAT batch's number drives the footer. Putting the cart's
  //      preview price ahead would silently override the batch row's
  //      visible price ($16.18) with the cart line's committed price
  //      ($17.13) and make the footer disagree with the batch row the
  //      cashier just clicked.
  //   3. /cart/preview subtotal/qty — kept as a fallback for
  //      non-batched products and refund-mode-adjacent cases where
  //      the modal lacks a batch context.
  //   4. Optimistic catalog estimate before any of the above resolve.
  const price = refundUnitPrice ?? batchUnitPrice ?? pricedFromCart ?? effectiveUnitPrice(product, selectedCustomer)

  // Publish to CFD. We mirror the modal's `price` variable directly so
  // the customer screen always shows the same number as the cashier's
  // Add-to-Cart button — including batch-tier prices, which an earlier
  // version missed (the CFD fell back to the catalog regular price for
  // batched products like 710 Hash Stiks). All upstream signals
  // (selectedCustomer, selectedBatchKey, previewItems, refund mode)
  // funnel through `price`, so a single dep covers all of them.
  useEffect(() => {
    broadcastProductShown(toFeaturedProduct(product, price))
    // product.id over the full product object — the parent may pass a
    // new reference per render, but we only need to re-fire on identity
    // changes (different product) or price changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id, price])

  // ---------- Discount math (PER-UNIT input) ----------
  // The discount input is the amount/percent off ONE unit. The line-level
  // figures shown in the footer are computed by multiplying by totalQty.
  const lineSubtotal = price * totalQty
  const parsedDiscountInput = useMemo(() => {
    const n = parseFloat(discountInput)
    return Number.isFinite(n) && n > 0 ? n : 0
  }, [discountInput])

  const {
    unitDiscountAmount,
    unitDiscountPercent,
    unitFinalPrice,
  } = useMemo(() => {
    if (parsedDiscountInput === 0 || price <= 0) {
      return { unitDiscountAmount: 0, unitDiscountPercent: 0, unitFinalPrice: price }
    }
    if (discountMode === 'percent') {
      const pct = Math.min(parsedDiscountInput, 100)
      const amt = +(price * (pct / 100)).toFixed(2)
      return {
        unitDiscountAmount: amt,
        unitDiscountPercent: pct,
        unitFinalPrice: +(price - amt).toFixed(2),
      }
    }
    const amt = Math.min(parsedDiscountInput, price)
    const pct = +((amt / price) * 100).toFixed(2)
    return {
      unitDiscountAmount: amt,
      unitDiscountPercent: pct,
      unitFinalPrice: +(price - amt).toFixed(2),
    }
  }, [parsedDiscountInput, discountMode, price])

  // Aggregate (line-level) numbers for the persistent footer.
  const lineDiscountAmount = +(unitDiscountAmount * totalQty).toFixed(2)
  const finalPrice = +(unitFinalPrice * totalQty).toFixed(2)

  const hasDiscount = unitDiscountAmount > 0
  const discountReady = hasDiscount && reasonId !== null
  // Refund lines carry a discount cloned from the source receipt — the
  // cashier didn't apply it, so don't ask them to justify it. We check
  // both the store flag AND the line's own sign because `isRefundMode`
  // is not persisted; a rehydrated refund cart can have negative-qty
  // lines while the flag has reset to false. (Outside refund context, a
  // cashier-entered discount without a reason still blocks submit.)
  const isRefundLine = (existingCartItem?.quantity ?? 0) < 0
  const discountAttempted = !isRefundMode && !isRefundLine && hasDiscount && reasonId === null
  // Below-cost guard (pos.disallow_price_lower_than_cost, default on): a
  // manual per-line discount may not drop the unit price under the
  // product's cost. Refund lines are exempt -- their discount is cloned
  // from the source sale, not cashier-applied. Pure client-side: we
  // already have unitFinalPrice and the product cost locally.
  //
  // `totalQty > 0` gate: only a line that actually sells units can be
  // "below cost". Zeroing the qty IS the modal's remove path (see
  // handleSubmit's `newQtyForOriginal === 0 -> removeFromCart`), so a
  // below-cost line must stay removable -- without this gate the guard
  // early-returns out of handleSubmit AND disables the footer button,
  // trapping a below-cost line (legacy/recalled cart, post-flip config)
  // in the cart.
  const unitCost = Number(product.cost ?? 0)
  // Multi-batch: submit applies the SAME per-unit discount to every selected
  // batch, each priced at its own rate (lots can differ). The binding line
  // for below-cost is the CHEAPEST selected batch, not the active one, so
  // check that batch's discounted unit price. Falls back to `price` when
  // there's no batch context.
  const lowestSelectedFinalPrice = (() => {
    let minUnit: number | null = null
    for (const [lotKey, q] of Object.entries(batchQty)) {
      if (q <= 0) continue
      let unit = price
      if (lotKey !== NO_BATCH_KEY && batches) {
        const b = batches.find((x) => x.lot_number === lotKey)
        const dp = typeof b?.discounted_price === 'number' && b.discounted_price > 0 ? b.discounted_price : null
        const bp = typeof b?.price === 'number' && b.price > 0 ? b.price : null
        unit = dp ?? bp ?? price
      }
      minUnit = minUnit == null ? unit : Math.min(minUnit, unit)
    }
    const base = minUnit ?? price
    // Submit applies a FIXED per-unit dollar discount (unitDiscountAmount,
    // derived from the ACTIVE batch) to every selected batch -- even in
    // percent mode only `discount_amount` goes on the wire (cartToRequest),
    // never a percent. So the real floor for the cheapest batch is
    // base - unitDiscountAmount, NOT base*(1-pct): a per-batch percent would
    // under-enforce on cheaper lots.
    return +(base - unitDiscountAmount).toFixed(2)
  })()
  const belowCost =
    totalQty > 0 &&
    !isRefundMode && !isRefundLine &&
    posConfig?.posDisallowPriceLowerThanCost !== false &&
    hasDiscount && unitCost > 0 && lowestSelectedFinalPrice < unitCost

  // ---------- Discount limit / manager approval ----------
  // A per-unit discount over the clerk's limit needs a manager PIN (the
  // backend gate is authoritative; this only routes the cashier to the
  // dialog up front). Refund lines are never gated. `approvalGrantedRef`
  // lets handleSubmit through once the PIN was accepted.
  const { limit: discountLimit, label: discountLimitText } = useDiscountLimit()
  // No approval path on a partially-paid receipt: the line can't change.
  const partiallyPaid = usePOSStore((s) => isReceiptPartiallyPaid(s.serverTotals, s.isRefundMode))
  const [showApproval, setShowApproval] = useState(false)
  // True only while the approval dialog is open: an approval that resolves
  // after the dialog was closed (possible while offline) must not apply.
  const approvalOpenRef = useRef(false)
  const approvalGrantedRef = useRef(false)
  // A recalled receipt is being asked whether it already carries an
  // approval for this value (spinner on the submit button).
  const [probing, setProbing] = useState(false)

  // Each lot's own unit price (lots can be priced differently): the batch's
  // discounted price, else its price, else the modal's price.
  const lotUnitPrice = useCallback((lotKey: string): number => {
    if (lotKey === NO_BATCH_KEY || !batches) return price
    const b = batches.find((x) => x.lot_number === lotKey)
    const dp = typeof b?.discounted_price === 'number' && b.discounted_price > 0 ? b.discounted_price : null
    const bp = typeof b?.price === 'number' && b.price > 0 ? b.price : null
    return dp ?? bp ?? price
  }, [batches, price])

  // Where each selected batch lands on submit: its cart lot, its unit price,
  // and the cart line it merges into (posStore.addToCart's strict
  // product+lot match) when it isn't the line being edited.
  const posCart = usePOSStore((s) => s.cart)
  const landings = useMemo(() => {
    const out: Array<{ lotNumber: string | null; q: number; unit: number; merged: typeof posCart[number] | null }> = []
    for (const [lotKey, q] of Object.entries(batchQty)) {
      if (q <= 0) continue
      const unit = lotUnitPrice(lotKey)
      if (isEditing && existingCartItem && lotKey === (existingCartItem.lot_number ?? NO_BATCH_KEY)) {
        out.push({ lotNumber: existingCartItem.lot_number ?? null, q, unit, merged: null })
        continue
      }
      const lotNumber = lotKey === NO_BATCH_KEY
        ? (isEditing ? null : (product.lot_number ?? null))
        : lotKeyToCartLot(lotKey)
      const merged = posCart.find((i) => i.product_id === product.id && i.lot_number === lotNumber
        && i.id !== existingCartItem?.id) ?? null
      out.push({ lotNumber, q, unit, merged })
    }
    return out
  }, [batchQty, isEditing, existingCartItem, product, posCart, lotUnitPrice])

  // The server's rule, per lot at its own price: the per-unit $ that goes
  // on the wire (even when entered as a percent), with any manual price on
  // the line judged together with it as one drop from list (combined_drop),
  // half a cent per unit of tolerance.
  const overLimit =
    !isRefundMode && !isRefundLine && !partiallyPaid && discountReady && totalQty > 0
    && landings.some(({ unit, q, merged }) => combinedDropExceedsLimit({
      list: unit,
      price: merged?.manual_price === true && !merged.gift_card_number ? merged.unit_price : null,
      unitDiscount: unitDiscountAmount,
      qty: merged ? merged.quantity + q : q,
    }, discountLimit))

  // Merged-line rule. Cart lines are keyed product|lot on the wire (the
  // preview bridge and /cart/preview both collapse a second line with the
  // same product and lot), so these units can't go on as a separate line
  // with their own discount: the per-unit discount is the WHOLE merged
  // line's. So the footer shows the merged line and the cashier must
  // confirm when:
  //  - 'change': this discount differs from the one on the units already
  //    rung, which would silently get it too;
  //  - 'inherit': no discount entered here, but the line already has one --
  //    posStore.addToCart gives the new units the same per-unit discount
  //    (while the footer above shows them undiscounted), and when that line
  //    was manager-approved the qty change needs a new PIN.
  const cartLines = useCartStore((s) => s.lines)
  const riding = useCartStore((s) => s.discountApprovals)
  const mergeChanges = useMemo(() => {
    if (isRefundMode || isRefundLine) return []
    const out: Array<{
      kind: 'change' | 'inherit'
      lotNumber: string | null
      rung: number
      total: number
      rungUnit: number
      approved: boolean
    }> = []
    for (const { lotNumber, q, merged } of landings) {
      if (!merged || merged.quantity <= 0) continue
      const rungUnit = (merged.discount_amount ?? 0) / Math.abs(merged.quantity)
      let kind: 'change' | 'inherit' | null = null
      if (discountReady) {
        if (toCents(rungUnit) !== toCents(unitDiscountAmount)) kind = 'change'
      } else if (rungUnit > 0) {
        kind = 'inherit'
      }
      if (!kind) continue
      const line = cartLines.find((l) => l.product_id === merged.product_id
        && (l.lot_number ?? null) === (merged.lot_number ?? null))
      const item = line ? lineApprovalItem(line) : null
      out.push({
        kind,
        lotNumber,
        rung: merged.quantity,
        total: merged.quantity + q,
        rungUnit,
        approved: item != null && isItemApproved(item, riding),
      })
    }
    return out
  }, [landings, discountReady, isRefundMode, isRefundLine, unitDiscountAmount, cartLines, riding])
  const mergeKey = mergeChanges.map((m) => `${m.kind}|${m.lotNumber}|${m.total}`).join(';') + `|${unitDiscountAmount}`
  const [mergeAckKey, setMergeAckKey] = useState<string | null>(null)
  const mergeNeedsAck = mergeChanges.length > 0 && mergeAckKey !== mergeKey

  // The cart as this submit will leave it, and the values the PIN covers
  // (one per line, listed in the dialog). Mirrors handleSubmit's writes:
  // each selected batch gets the per-unit discount on its WHOLE line --
  // including a merged line (qty old + q) -- so the tuple approved here is
  // the one that actually goes on the wire.
  const buildApprovalPlan = useCallback((): {
    nextCart: Cart
    items: ApprovalItem[]
  } => {
    const name = product.display_name || product.description || product.name || ''
    const targets: LineDiscountTarget[] = landings.map(({ lotNumber, q, merged }) => {
      const qty = merged ? merged.quantity + q : q
      return {
        product_id: product.id,
        lot_number: lotNumber,
        qty,
        discount_amount: +(unitDiscountAmount * qty).toFixed(2),
        discount_reason_id: reasonId,
        discount_reason_other: null,
        description: name,
      }
    })
    const nextCart = cartWithLineDiscounts(cartSnapshot(), targets)
    const items: ApprovalItem[] = []
    for (const t of targets) {
      const line = nextCart.lines.find(
        (l) => l.product_id === t.product_id && (l.lot_number ?? null) === t.lot_number,
      )
      const item = line ? lineApprovalItem({ ...line, display: { ...line.display, description: name } }) : null
      if (item && !items.some((i) => i.signature === item.signature)) items.push(item)
    }
    return { nextCart, items }
  }, [landings, product, unitDiscountAmount, reasonId])

  // Over the limit AND not already approved at exactly this tuple (e.g.
  // re-opening an approved line to change its note).
  const discountApprovals = useCartStore((s) => s.discountApprovals)
  const approvalPlan = useMemo(
    () => (overLimit ? buildApprovalPlan() : null),
    [overLimit, buildApprovalPlan],
  )
  const needsApproval = useMemo(
    () => approvalPlan != null && !allApproved(approvalPlan.items),
    // discountApprovals: allApproved() reads it from the store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [approvalPlan, discountApprovals],
  )
  // An item discount is off the wire while a cart discount is on, so no PIN
  // could approve it: say so up front instead of after a manager typed one.
  const activeCartDiscount = useCartStore((s) => s.discount)
  const cartDiscountBlocksApproval = needsApproval && activeCartDiscount != null

  // ---------- Quantity helpers ----------
  // Per-key on-hand cap. Batches expose `quantity`; for the non-batch case we
  // fall back to the product's on_hand / quantity_available, defaulting to
  // Infinity when stock isn't known so we don't accidentally block sales.
  // In refund mode the cap is the originally-purchased qty from the source
  // receipt (`max_refund_qty`) — refunding 2 of an item where 1 was sold
  // is meaningless and the BE will reject it.
  const getMaxForKey = useCallback(
    (key: string): number => {
      if (isRefundMode && refundLine) {
        const refundCap = refundLine.max_refund_qty ?? Math.abs(refundLine.quantity)
        return Math.max(0, Math.floor(refundCap))
      }
      // Oversell allowed: skip the on-hand cap entirely. Refund cap above
      // still applies — that's a server-enforced limit, not a stock check.
      if (!verifyOverOnHand) return Infinity
      if (key === NO_BATCH_KEY) {
        const onHand = product.on_hand ?? product.quantity_available ?? null
        return onHand != null && Number.isFinite(Number(onHand))
          ? Math.max(0, Math.floor(Number(onHand)))
          : Infinity
      }
      const batch = batches?.find((b) => b.lot_number === key)
      return batch ? Math.max(0, Math.floor(batch.quantity)) : Infinity
    },
    [batches, product.on_hand, product.quantity_available, isRefundMode, refundLine, verifyOverOnHand],
  )

  const setBatchQuantity = useCallback(
    (key: string, qty: number) => {
      const max = getMaxForKey(key)
      const safe = Math.min(max, Math.max(0, Math.floor(qty)))
      setBatchQty((prev) => {
        if (isRefundMode) {
          // A refund maps to one source-receipt line — mutating any batch
          // zeros every other batch so only one is active at a time.
          const next: Record<string, number> = {}
          for (const k of Object.keys(prev)) next[k] = 0
          next[key] = safe
          return next
        }
        return { ...prev, [key]: safe }
      })
    },
    [getMaxForKey, isRefundMode],
  )

  // Row-click wrapper. In refund mode, picking a different batch
  // transfers the active qty onto the new lot rather than leaving the
  // old one with stale qty (the cashier wants to swap which batch the
  // refund pulls from, not split the refund across batches).
  const handleSelectBatch = useCallback(
    (newKey: string) => {
      if (isRefundMode && newKey !== selectedBatchKey) {
        setBatchQty((prev) => {
          const current = prev[selectedBatchKey] ?? 0
          const next: Record<string, number> = {}
          for (const k of Object.keys(prev)) next[k] = 0
          next[newKey] = current > 0 ? current : 1
          return next
        })
      }
      setSelectedBatchKey(newKey)
    },
    [isRefundMode, selectedBatchKey],
  )

  const bumpSelected = useCallback(
    (delta: number) => {
      const max = getMaxForKey(selectedBatchKey)
      setBatchQty((prev) => {
        const current = prev[selectedBatchKey] ?? 0
        const next = Math.min(max, Math.max(0, current + delta))
        if (next === current) return prev
        return { ...prev, [selectedBatchKey]: next }
      })
    },
    [selectedBatchKey, getMaxForKey],
  )

  // Whether the footer + button should be disabled (active row is at on-hand).
  const selectedAtMax = useMemo(() => {
    const current = batchQty[selectedBatchKey] ?? 0
    return current >= getMaxForKey(selectedBatchKey)
  }, [batchQty, selectedBatchKey, getMaxForKey])

  // ---------- Compute the per-batch line discount ----------
  // Both percent and dollar modes are now linear per unit, so each batch line
  // gets `unitDiscountAmount × batchQty` (no proportional split needed).
  const computeLineDiscount = useCallback(
    (batchQuantity: number) => {
      if (!discountReady) return { amount: 0, percent: 0 }
      return {
        amount: +(unitDiscountAmount * batchQuantity).toFixed(2),
        percent: unitDiscountPercent,
      }
    },
    [discountReady, unitDiscountAmount, unitDiscountPercent]
  )

  // ---------- Submit: add new lines OR update the existing cart line ----------
  const handleSubmit = useCallback(async () => {
    // Refund-mutation preflight: refuse if any refund-item PUT/DELETE
    // is already in flight, whether kicked off by THIS modal (local
    // .isPending) or by another caller seen via the shared mutation
    // key (isRefundMutating). The submit button's `disabled` predicate
    // covers the click path; this guard covers the Enter-key handler
    // around line 872 (which calls handleSubmit() directly, bypassing
    // the DOM-level disable) and any future programmatic caller.
    // Without it, an Enter press while greyed-out would queue a
    // second PUT in parallel with the in-flight one -- out-of-order
    // reseeds would then leave cart-qty desynced from serverTotals.
    if (updateReceiptItem.isPending || deleteReceiptItem.isPending) return
    if (isRefundMode && isRefundMutating) return

    // Synchronous re-entry latch (see submitInFlightRef declaration
    // above). Acquired AFTER the state-based guards (which are
    // broader -- they refuse cross-component mutations) but BEFORE
    // any state mutation or BE call in the body. A tight double-click
    // on Save (or Enter + click) fires both handlers in the same
    // tick, before mutation.isPending / isRefundMutating have a
    // chance to flip; without the ref latch each handler would
    // independently dispatch a PUT/DELETE, producing two parallel
    // requests with potentially divergent BE-applied defaults.
    // Released in the finally so the cashier can retry after a
    // transient mutation error.
    if (submitInFlightRef.current) return
    submitInFlightRef.current = true

    try {
      if (totalQty === 0 && !isEditing) return
      if (discountAttempted) {
        setActiveTab('discount')
        return
      }
      if (belowCost) {
        setActiveTab('discount')
        return
      }
      // The cashier hasn't confirmed the discount applies to the units
      // already rung (see mergeChanges).
      if (mergeNeedsAck) return
      if (cartDiscountBlocksApproval && !approvalGrantedRef.current) return
      if (needsApproval && !approvalGrantedRef.current) {
        // A recalled/unheld receipt may already carry an approval for this
        // value (the server reuses it): let the server decide, and only ask
        // for a PIN on its 409. Otherwise ask up front.
        if (usePOSStore.getState().currentReceiptId == null) {
          approvalOpenRef.current = true
          setShowApproval(true)
          return
        }
        setProbing(true)
        let verdict: Awaited<ReturnType<typeof probeDiscount>>
        try {
          verdict = await probeDiscount(buildApprovalPlan().nextCart)
        } finally {
          setProbing(false)
        }
        if (verdict === 'needs_approval') {
          approvalOpenRef.current = true
          setShowApproval(true)
          return
        }
      }

      const reason = reasonId !== null ? reasons.find((r) => r.id === reasonId) : null
      const noteText = reason ? `Discount: ${reason.name}` : null
      const entries = Object.entries(batchQty).filter(([, q]) => q > 0)

      // Refund mode: one source-receipt line maps to one cart line.
      // Update the existing refund line's lot (if cashier swapped batches)
      // and qty (negative — refunds are negative-qty cart lines). No
      // splitting into multiple lines, no addToCart calls.
      //
      // /cart/preview is skipped in refund mode (preview rejects negative
      // qty), so we round-trip the change to the refund receipt directly:
      // PUT for qty/lot edits, DELETE for clears. The Receipt response
      // carries refreshed totals that we feed into serverTotals so tax
      // and total stay accurate after a batch swap.
      if (isRefundMode && existingCartItem) {
        const activeEntry = entries[0] ?? null
        const receiptItemId = existingCartItem.receipt_item_id ?? null
        const previousLot = existingCartItem.lot_number ?? null
        const previousQty = Math.abs(existingCartItem.quantity)

        const reseedTotalsFromReceipt = (r: { totals?: Record<string, unknown>; amount_to_pay?: unknown }) => {
          const t = r.totals ?? {}
          const num = (v: unknown): number => {
            const n = typeof v === 'number' ? v : parseFloat(String(v ?? 0))
            return Number.isFinite(n) ? n : 0
          }
          // Refund totals come back positive from the BE; flip the sign
          // to match the cart's negative-qty convention.
          const subtotal = -Math.abs(num(t.subtotal))
          const taxes = -Math.abs(extractTaxes(t))
          const deposit = -Math.abs(num((t as { deposit_amount?: unknown }).deposit_amount))
          const total = -Math.abs(num(t.total))
          const amountToPay = -Math.abs(num(r.amount_to_pay ?? t.total))
          setServerTotals({ subtotal, taxes, deposit, total, amountToPay })
        }

        if (!activeEntry) {
          // Empty out → DELETE the receipt item, then drop the local cart line.
          if (receiptItemId != null && refundReceiptId != null) {
            try {
              const updated = await deleteReceiptItem.mutateAsync({
                receiptId: refundReceiptId,
                itemId: receiptItemId,
              })
              reseedTotalsFromReceipt(updated as unknown as { totals?: Record<string, unknown>; amount_to_pay?: unknown })
            } catch (err) {
              console.warn('[ProductModal] refund DELETE failed', err)
            }
          }
          removeFromCart(existingCartItem.id)
          onClose()
          return
        }

        const [lotKey, qty] = activeEntry
        const newLot = lotKey === NO_BATCH_KEY ? null : lotKeyToCartLot(lotKey)
        const lotChanged = (newLot ?? null) !== previousLot
        const qtyChanged = qty !== previousQty

        // Always update the local cart line so the UI reflects the
        // cashier's intent immediately, even if the BE call is slow.
        updateCartItem(existingCartItem.id, {
          quantity: -qty,
          lot_number: newLot,
        })

        if ((lotChanged || qtyChanged) && receiptItemId != null && refundReceiptId != null) {
          try {
            // PUT /receipts/{id}/items/{item_id} reads `batch_number`
            // from the body -- see update_from_request at
            // project/frontend/models/pos.py:5075:
            //     batch_number = body.get('batch_number')
            //     ...
            //     if batch_number: jitem.batch_number = batch_number
            // Sending `lot_number` here would be silently swallowed by
            // **body in apply_discounts (which takes **kwargs) and the
            // cashier's lot swap would never land on the BE receipt --
            // the local cart row would render the new lot but the
            // persisted receipt would still carry the source-receipt's
            // batch. We keep `lot_number` everywhere else in the FE
            // (cart shape, types, GS1 parser) because it matches the
            // user-facing concept; only this wire payload uses the BE
            // field name.
            const data: { qty?: number; batch_number?: string | null } = {}
            if (qtyChanged) data.qty = qty
            if (lotChanged) data.batch_number = newLot
            const updated = await updateReceiptItem.mutateAsync({
              receiptId: refundReceiptId,
              itemId: receiptItemId,
              data,
            })
            reseedTotalsFromReceipt(updated as unknown as { totals?: Record<string, unknown>; amount_to_pay?: unknown })
          } catch (err) {
            console.warn('[ProductModal] refund PUT failed', err)
          }
        }

        onClose()
        return
      }

      if (isEditing && existingCartItem) {
        const originalLot = existingCartItem.lot_number
        const originalKey = originalLot ?? NO_BATCH_KEY
        const newQtyForOriginal = batchQty[originalKey] ?? 0

        // 1) Original line: update qty (and discount), or remove if zeroed.
        if (newQtyForOriginal > 0) {
          const { amount, percent } = computeLineDiscount(newQtyForOriginal)
          updateCartItem(existingCartItem.id, {
            quantity: newQtyForOriginal,
            discount_amount: amount,
            discount_percent: percent,
            discount_reason_id: discountReady ? reasonId : null,
            discount_reason_other: null,
            notes: noteText ?? (discountReady ? null : existingCartItem.notes),
          })
        } else {
          removeFromCart(existingCartItem.id)
        }

        // 2) Any *other* batches with qty > 0 become brand-new cart lines.
        for (const [lotKey, qty] of entries) {
          if (lotKey === originalKey) continue
          const lotNumber = lotKeyToCartLot(lotKey)
          addToCart(product, qty, lotNumber)
          if (discountReady) {
            const cart = usePOSStore.getState().cart
            const target = [...cart].reverse().find(
              (i) => i.product_id === product.id && i.lot_number === lotNumber
            )
            if (target) {
              // addToCart may have MERGED into an existing same-lot line
              // (qty old + q): the per-unit discount covers the whole line,
              // matching buildApprovalPlan's signature.
              const { amount, percent } = computeLineDiscount(target.quantity)
              updateCartItem(target.id, {
                discount_amount: amount,
                discount_percent: percent,
                discount_reason_id: reasonId,
                discount_reason_other: null,
                notes: noteText ?? target.notes,
              })
            }
          }
        }
      } else {
        // Add fresh — one cart line per non-zero batch.
        for (const [lotKey, qty] of entries) {
          const lotNumber =
            lotKey === NO_BATCH_KEY
              ? (product.lot_number ?? null)
              : lotKeyToCartLot(lotKey)
          addToCart(product, qty, lotNumber)
          if (discountReady) {
            const cart = usePOSStore.getState().cart
            const target = [...cart].reverse().find(
              (i) => i.product_id === product.id && i.lot_number === lotNumber
            )
            if (target) {
              // addToCart may have MERGED into an existing same-lot line
              // (qty old + q): the per-unit discount covers the whole line,
              // matching buildApprovalPlan's signature.
              const { amount, percent } = computeLineDiscount(target.quantity)
              updateCartItem(target.id, {
                discount_amount: amount,
                discount_percent: percent,
                discount_reason_id: reasonId,
                discount_reason_other: null,
                notes: noteText ?? target.notes,
              })
            }
          }
        }
      }

      onClose()
    } finally {
      submitInFlightRef.current = false
    }
  }, [
    totalQty,
    isEditing,
    existingCartItem,
    discountAttempted,
    belowCost,
    discountReady,
    batchQty,
    addToCart,
    updateCartItem,
    removeFromCart,
    onClose,
    product,
    reasons,
    reasonId,
    computeLineDiscount,
    updateReceiptItem,
    deleteReceiptItem,
    isRefundMode,
    isRefundMutating,
    needsApproval,
    buildApprovalPlan,
    mergeNeedsAck,
    cartDiscountBlocksApproval,
  ])

  // Manager PIN entered for an over-limit discount: price the cart as it
  // will be after this submit (every selected batch gets the per-unit
  // discount), with the PIN on those lines. Accepted -> run the submit;
  // refused -> the dialog shows why.
  const approveAndSubmit = useCallback(async (pin: string) => {
    const { nextCart, items } = buildApprovalPlan()
    const { recorded } = await approveDiscount(nextCart, items, pin)
    // Closed while the check was in flight (offline): don't apply behind
    // the cashier's back, and drop what it recorded.
    if (!approvalOpenRef.current) {
      discardApprovals(recorded)
      return
    }
    approvalGrantedRef.current = true
    try {
      await handleSubmit()
    } finally {
      approvalGrantedRef.current = false
    }
  }, [buildApprovalPlan, handleSubmit])

  const handleApplyPriceMatch = useCallback(
    (store: NearbyStore) => {
      const delta = +(price - store.price).toFixed(2)
      if (delta <= 0) return
      setDiscountMode('amount')
      setDiscountInput(delta.toFixed(2))
      setReasonId((prev) => {
        if (prev !== null) return prev
        if (reasons.length === 0) return null
        const matched = reasons.find((r) =>
          /price\s*match|competitor|match/i.test(r.name),
        )
        return matched?.id ?? reasons[0].id
      })
      setActiveTab('discount')
    },
    [price, reasons],
  )

  // ---------- Keyboard ----------
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      } else if (e.key === 'Enter' && activeTab !== 'discount' && activeTab !== 'nearby') {
        // Don't hijack Enter while typing into the discount or nearby search inputs
        e.preventDefault()
        handleSubmit()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose, handleSubmit, activeTab])

  // ---------- Tabs ----------
  const tabs: TabDef[] = [
    { id: 'overview', label: 'Overview', icon: LayoutGrid, show: true },
    {
      id: 'batch',
      label: 'Batch',
      icon: Package,
      show: hasBatches,
      badge: hasBatches ? batches!.length : undefined,
    },
    {
      id: 'discount',
      label: 'Discount',
      icon: Tag,
      // Hidden in refund mode — pricing on a refund receipt mirrors
      // the source sale 1:1 and is not editable. Showing the tab would
      // imply otherwise.
      show: !isRefundMode,
      badge: hasDiscount ? '•' : undefined,
    },
    { id: 'nearby', label: 'Nearby', icon: MapPin, show: true, disabled: true },
  ]

  useEffect(() => {
    if (!tabs.find((t) => t.id === activeTab && t.show)) {
      setActiveTab('overview')
    }
  }, [activeTab, tabs])

  // ---------- Use Overview's natural height as a floor for the Batch tab ----------
  // Discount / Nearby stay compact; only Batch borrows Overview's height so it
  // doesn't feel cramped when there are only a couple of batches.
  const contentRef = useRef<HTMLDivElement>(null)
  const [overviewHeight, setOverviewHeight] = useState<number>(0)

  useLayoutEffect(() => {
    if (activeTab !== 'overview') return
    const node = contentRef.current
    if (!node) return
    const measure = () => {
      const h = node.scrollHeight
      setOverviewHeight((prev) => (h > prev ? h : prev))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(node)
    return () => ro.disconnect()
  }, [activeTab])

  const contentMinHeight = activeTab === 'batch' && overviewHeight > 0 ? overviewHeight : undefined

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-2xl w-full mx-4 shadow-2xl animate-scale-in flex flex-col max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ---------- Persistent header (full width) ---------- */}
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-14 h-14 bg-green-50 rounded-xl flex items-center justify-center text-green-600 overflow-hidden shrink-0">
              {p.image && !imgError ? (
                <img src={withCdnImageWidth(p.image, 150)} alt={p.name} className="w-full h-full object-cover" onError={() => setImgError(true)} />
              ) : (
                <ProductIcon name={p.icon} size={28} />
              )}
            </div>
            <div className="min-w-0 select-text">
              <h2 className="text-lg font-bold text-gray-900 leading-tight truncate">{p.name}</h2>
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-1.5">
                {p.brand && <span className="truncate">{p.brand}</span>}
                {p.brand && <span className="text-gray-300">·</span>}
                {(product.sku || product.cspcid) && (
                  <span
                    className="inline-flex items-center px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[11px] font-mono font-medium rounded border border-gray-200 truncate max-w-[140px]"
                    title={`SKU: ${product.sku || product.cspcid}`}
                  >
                    {product.sku || product.cspcid}
                  </span>
                )}
                {(() => {
                  // IOH badge — sits next to SKU so the cashier sees stock
                  // and identity together. Mirrors the fallback chain
                  // ProductCard uses (on_hand ?? quantity_available ??
                  // quantity) so this stays in sync when only one of the
                  // three fields is populated. Red below 2 to match
                  // ProductCard / CartItemRow's low-stock styling.
                  const raw = product.on_hand ?? product.quantity_available ?? product.quantity ?? null
                  const num = raw == null ? null : Number(raw)
                  if (num == null || !Number.isFinite(num)) return null
                  const onHand = Math.max(0, Math.floor(num))
                  return (
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 text-[11px] font-medium rounded border ${
                        onHand <= 1
                          ? 'bg-red-50 text-red-600 border-red-100 font-semibold'
                          : 'bg-gray-100 text-gray-600 border-gray-200'
                      }`}
                      title={`In-stock on hand: ${onHand}`}
                    >
                      IOH: {onHand}
                    </span>
                  )
                })()}
                {/* <span className={`font-semibold ${product.on_sale ? 'text-red-600' : 'text-gray-700'}`}>
                  {formatCurrency(price)}
                </span>
                {product.on_sale && comparePrice && comparePrice > price && (
                  <span className="text-xs text-gray-400 line-through">{formatCurrency(comparePrice)}</span>
                )} */}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer shrink-0"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* ---------- Body: nav rail + (content + footer) ---------- */}
        <div className="flex flex-1 min-h-0">
          {/* Vertical tab rail (full-height, sits beside content + footer) */}
          <nav className="w-20 bg-gray-50 border-r border-gray-100 py-3 flex flex-col gap-1 shrink-0">
            {tabs.filter((t) => t.show).map((tab) => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={() => !tab.disabled && setActiveTab(tab.id)}
                  disabled={tab.disabled}
                  className={`relative mx-2 py-3 rounded-lg flex flex-col items-center gap-1 transition-colors ${
                    isActive
                      ? 'bg-white text-green-600 shadow-sm border border-gray-200'
                      : tab.disabled
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-white cursor-pointer'
                  }`}
                  title={tab.disabled ? `${tab.label} (coming soon)` : tab.label}
                >
                  <Icon size={20} />
                  <span className="text-[10px] font-medium leading-none">{tab.label}</span>
                  {tab.badge !== undefined && (
                    <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 bg-green-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {tab.badge}
                    </span>
                  )}
                  {tab.disabled && (
                    <Clock size={10} className="absolute top-1.5 right-1.5 text-gray-400" />
                  )}
                </button>
              )
            })}
          </nav>

          {/* Right column: tab content + footer (footer scoped to this width) */}
          <div className="flex-1 flex flex-col min-w-0">
            <div
              ref={contentRef}
              className="flex-1 overflow-y-auto p-5"
              style={contentMinHeight ? { minHeight: contentMinHeight } : undefined}
            >
              {activeTab === 'overview' && (
                <OverviewPanel
                  product={product}
                  fields={p}
                  activeBatch={activeBatch}
                />
              )}
              {activeTab === 'batch' && hasBatches && batches && (
                <BatchPanel
                  batches={batches}
                  batchQty={batchQty}
                  selectedKey={selectedBatchKey}
                  onSelect={handleSelectBatch}
                  onSetQty={setBatchQuantity}
                  unitPrice={price}
                />
              )}
              {activeTab === 'discount' && (
                <DiscountPanel
                  limitLabel={isRefundMode ? null : discountLimitText}
                  unitPrice={price}
                  discountMode={discountMode}
                  setDiscountMode={setDiscountMode}
                  discountInput={discountInput}
                  setDiscountInput={setDiscountInput}
                  unitDiscountAmount={unitDiscountAmount}
                  unitDiscountPercent={unitDiscountPercent}
                  unitFinalPrice={unitFinalPrice}
                  reasons={reasons}
                  reasonId={reasonId}
                  setReasonId={setReasonId}
                />
              )}
              {activeTab === 'nearby' && (
                <NearbyPanel unitPrice={price} onSelectStore={handleApplyPriceMatch} />
              )}
            </div>

            {/* ---------- Footer (only spans content width) ---------- */}
            <div className="border-t border-gray-100 p-4 bg-gray-50">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => bumpSelected(-1)}
                    className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-50"
                    disabled={(batchQty[selectedBatchKey] ?? 0) <= 0}
                    aria-label="Decrease quantity"
                  >
                    <Minus size={18} />
                  </button>
                  <span className="text-xl font-bold w-10 text-center tabular-nums">{totalQty}</span>
                  <button
                    onClick={() => bumpSelected(1)}
                    className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={selectedAtMax}
                    title={selectedAtMax ? 'Only on-hand quantity available' : undefined}
                    aria-label="Increase quantity"
                  >
                    <Plus size={18} />
                  </button>
                </div>
                <div className="text-right">
                  {hasDiscount && (
                    <p className="text-xs text-gray-400 line-through leading-none">
                      {formatCurrency(lineSubtotal)}
                    </p>
                  )}
                  <p className={`text-2xl font-bold leading-tight ${hasDiscount ? 'text-green-600' : 'text-gray-900'}`}>
                    {formatCurrency(finalPrice)}
                  </p>
                  {hasDiscount && (
                    <p className="text-xs text-green-600 leading-none">
                      Save {formatCurrency(lineDiscountAmount)}
                    </p>
                  )}
                </div>
              </div>

              {discountAttempted && (
                <p className="text-xs text-amber-600 mb-2 text-center">
                  Select a discount reason on the Discount tab to continue.
                </p>
              )}

              {belowCost && (
                <p className="text-xs text-red-600 mb-2 text-center">
                  This discount prices the item below cost ({formatCurrency(unitCost)}). Reduce the discount to continue.
                </p>
              )}

              {cartDiscountBlocksApproval && (
                <p className="text-xs text-amber-700 mb-2 text-center">{REMOVE_CART_DISCOUNT_FIRST}</p>
              )}

              {mergeChanges.map((m) => (
                <div
                  key={`${m.kind}|${m.lotNumber ?? ''}`}
                  className="mb-2 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-900"
                >
                  {m.kind === 'change' ? (
                    <p>
                      {m.lotNumber ? `Lot ${m.lotNumber}: ` : ''}already {m.rung} in cart. The discount
                      ({formatCurrency(unitDiscountAmount)} off each) applies to all {m.total}:
                      line total <span className="font-semibold">{formatCurrency(unitFinalPrice * m.total)}</span>.
                    </p>
                  ) : (
                    <p>
                      {m.lotNumber ? `Lot ${m.lotNumber}: ` : ''}{m.rung} in cart at {formatCurrency(m.rungUnit)} off
                      each. The new {m.total - m.rung === 1 ? 'unit gets' : 'units get'} the same discount
                      (line of {m.total}).{m.approved ? ' That discount was manager-approved for ' + m.rung + ', so a new PIN is needed.' : ''}
                    </p>
                  )}
                </div>
              ))}
              {mergeChanges.length > 0 && (
                <label className="mb-3 flex items-center justify-center gap-2 text-xs font-medium text-amber-900 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!mergeNeedsAck}
                    onChange={(e) => setMergeAckKey(e.target.checked ? mergeKey : null)}
                    className="w-4 h-4 accent-amber-600"
                  />
                  {mergeChanges.length === 1
                    ? `Apply to all ${mergeChanges[0].total} in cart`
                    : 'Apply to every unit already in cart'}
                </label>
              )}

              <button
                onClick={handleSubmit}
                className={`w-full py-3 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 cursor-pointer ${
                  discountAttempted || belowCost || mergeNeedsAck || probing || cartDiscountBlocksApproval || (totalQty === 0 && !isEditing)
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed hover:bg-gray-300'
                    : isEditing && totalQty === 0
                      ? 'bg-red-500 hover:bg-red-600 text-white'
                      : needsApproval
                        ? 'bg-amber-500 hover:bg-amber-600 text-white'
                        : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
                disabled={
                  discountAttempted
                  || belowCost
                  || mergeNeedsAck
                  || probing
                  || cartDiscountBlocksApproval
                  || (totalQty === 0 && !isEditing)
                  || updateReceiptItem.isPending
                  || deleteReceiptItem.isPending
                  || (isRefundMode && isRefundMutating)
                }
              >
                {updateReceiptItem.isPending || deleteReceiptItem.isPending || (isRefundMode && isRefundMutating)
                  ? <>Saving…</>
                  : probing
                    ? <><Loader2 size={20} className="animate-spin" /> Checking approval…</>
                  : needsApproval
                    ? <>Needs manager approval</>
                  : isEditing
                    ? totalQty === 0
                      ? <><Trash2 size={20} /> Remove from Cart</>
                      : <><Check size={20} /> Update Cart — {formatCurrency(finalPrice)}</>
                    : <>Add {totalQty} to Cart — {formatCurrency(finalPrice)}</>}
              </button>
            </div>
          </div>
        </div>
      </div>
      <ManagerApprovalDialog
        isOpen={showApproval}
        onClose={() => {
          approvalOpenRef.current = false
          setShowApproval(false)
        }}
        title="Approve discount"
        subtitle={approvalSubtitle(discountLimit, unitDiscountPercent)}
        items={approvalRows(approvalPlan?.items ?? [])}
        onSubmit={approveAndSubmit}
      />
    </div>
  )
}

// ============================================================
// Tab panels
// ============================================================

interface OverviewPanelProps {
  product: Product
  fields: ReturnType<typeof getProductFields>
  // Currently-active batch for this modal session (the row highlighted
  // in the Batch tab). When set, its COA-tested THC/CBD overrides the
  // product-level averages here so the Overview matches what's
  // actually going into the cart. Falls back to the product values
  // when no batch is selected or the batch lacks recorded potency.
  activeBatch: ProductLot | null
}

function OverviewPanel({ product, fields: p, activeBatch }: OverviewPanelProps) {
  const thc = activeBatch?.thc != null ? activeBatch.thc : p.thc
  const cbd = activeBatch?.cbd != null ? activeBatch.cbd : p.cbd
  const unit =
    activeBatch && (activeBatch.thc != null || activeBatch.cbd != null)
      ? (activeBatch.test_units ?? null)
      : p.unit

  // Reserve the compact 3-col row for the cannabis-y trio (THC/CBD/Weight).
  // For non-cannabis items it would render Weight alone with two empty
  // columns, so promote Weight down to sit beside Type/Strain/Category.
  const showPotencyRow = thc !== null || cbd !== null
  // Type / Strain / Category in a fixed 3-col row. Each tile is only
  // pushed when the underlying value is meaningful (non-empty, not "-",
  // not "N/A"), so an empty Strain just collapses to a 2-tile row
  // rather than rendering a "Strain -" placeholder.
  const triRowTiles: Array<{ label: string; value: string; valueClass?: string }> = []
  if (isMeaningfulValue(p.typeName)) {
    triRowTiles.push({ label: 'Type', value: p.typeName, valueClass: typeValueTextClass(p.typeName) })
  }
  if (isMeaningfulValue(p.strain)) {
    triRowTiles.push({ label: 'Strain', value: p.strain })
  }
  if (isMeaningfulValue(p.category)) {
    triRowTiles.push({ label: 'Category', value: p.category })
  }

  // Weight (when not in the potency row) and SKU live in a separate
  // row below — they're not part of the strain trio.
  const extraTiles: Array<{ label: string; value: string }> = []
  if (!showPotencyRow && p.size) extraTiles.push({ label: 'Weight', value: p.size })
  if (product.sku) extraTiles.push({ label: 'SKU', value: product.sku })

  return (
    <div className="space-y-4 min-h-[200px]">
      {p.description && <DescriptionBlock text={p.description} />}

      {showPotencyRow && (
        <div className="grid grid-cols-3 gap-3">
          {thc !== null && (
            <Tile label="THC" value={`${formatPercent(thc)}${potencySuffix(unit)}`} valueClass="text-green-600" />
          )}
          {cbd !== null && (
            <Tile label="CBD" value={`${formatPercent(cbd)}${potencySuffix(unit)}`} valueClass="text-blue-600" />
          )}
          {p.size && <Tile label="Weight" value={p.size} />}
        </div>
      )}

      {triRowTiles.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {triRowTiles.map((t) => (
            <Tile key={t.label} label={t.label} value={t.value} valueClass={t.valueClass} />
          ))}
        </div>
      )}

      {extraTiles.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {extraTiles.map((t) => (
            <Tile key={t.label} label={t.label} value={t.value} />
          ))}
        </div>
      )}
    </div>
  )
}

function Tile({ label, value, valueClass = '' }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="bg-gray-50 p-3 rounded-lg">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`font-semibold ${valueClass}`}>{value}</p>
    </div>
  )
}

// Long descriptions clamp to 5 lines with a "Show more" toggle. The
// toggle only renders when the unclamped text actually exceeds the
// clamp height, so short descriptions still read normally without a
// dangling button. Measurement runs in useLayoutEffect so the button
// state is correct on first paint (avoids a one-frame flash).
function DescriptionBlock({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const ref = useRef<HTMLParagraphElement>(null)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    // Re-clamp on text change so switching products inside the modal
    // re-evaluates whether the toggle is needed. Reset expanded state
    // for the same reason.
    setExpanded(false)
    // line-clamp uses overflow:hidden, so scrollHeight reflects the
    // full content height while clientHeight is the clamped height.
    // 1px slack absorbs sub-pixel rounding on hidpi displays.
    setIsOverflowing(el.scrollHeight - 1 > el.clientHeight)
  }, [text])

  return (
    <div>
      <p className="text-xs text-gray-500 mb-1">Description</p>
      <p
        ref={ref}
        className={`text-sm text-gray-700 leading-relaxed ${expanded ? '' : 'line-clamp-5'}`}
      >
        {text}
      </p>
      {isOverflowing && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-xs font-medium text-green-600 hover:text-green-700 cursor-pointer"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  )
}

// ----- Batch panel: per-row qty + select + THC/CBD -----

interface BatchPanelProps {
  batches: ProductLot[]
  batchQty: Record<string, number>
  selectedKey: string
  onSelect: (key: string) => void
  onSetQty: (key: string, qty: number) => void
  // Customer-aware per-unit price the cart will charge for this product.
  // Same value used by the modal footer; surfaced per batch so the cashier
  // sees what each lot will ring up at without the price-card precedence
  // (regular / member / scheduled-discount) being repeated downstream.
  unitPrice: number
}

function BatchPanel({ batches, batchQty, selectedKey, onSelect, onSetQty, unitPrice }: BatchPanelProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-medium text-gray-700 uppercase tracking-wide">
          Available batches ({batches.length})
        </p>
        <p className="text-[11px] text-gray-400">Tap a row to make it the active batch</p>
      </div>

      {batches.map((b) => {
        const qty = batchQty[b.lot_number] ?? 0
        const isSelected = selectedKey === b.lot_number
        const isMaxed = qty >= b.quantity
        // Per-batch customer price wins over the modal-level fallback.
        // `discounted_price` is the customer-tier-resolved number when
        // the request included `customer_id`; `price` is the catalog
        // price for this specific lot. Falling back to `unitPrice` keeps
        // the row useful for legacy responses with neither field set.
        const rowPrice =
          (typeof b.discounted_price === 'number' && b.discounted_price > 0
            ? b.discounted_price
            : null) ??
          (typeof b.price === 'number' && b.price > 0 ? b.price : null) ??
          unitPrice
        return (
          <button
            type="button"
            key={b.lot_number}
            onClick={() => onSelect(b.lot_number)}
            className={`select-text w-full text-left p-3 rounded-lg border transition-colors ${
              isSelected
                ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-200'
                : 'bg-white border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm text-gray-900 truncate">
                  {isSyntheticLot(b.lot_number) ? '' : b.lot_number}
                </p>
                <div className="grid grid-cols-[72px_140px] gap-x-2 text-[11px] text-gray-500 mt-1 whitespace-nowrap">
                  <span>{b.quantity} On Hand</span>
                  <span>
                    {b.expiration_date
                      ? `Date: ${new Date(b.expiration_date).toLocaleDateString('en-CA', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}`
                      : '\u00A0'}
                  </span>
                </div>
              </div>

              {/* THC/CBD stack + customer-aware price. Sits between the
                  lot/metadata block and the qty stepper, vertically centered
                  by the outer flex's `items-center` so the row's height
                  doesn't grow. */}
              <div className="flex items-center gap-4 shrink-0 whitespace-nowrap">
                <div className="flex flex-col leading-tight text-[11px] text-right">
                  <span className="text-green-600 font-medium">
                    {b.thc !== null && b.thc !== undefined
                      ? `THC ${formatPercent(b.thc)}${potencySuffix(b.test_units)}`
                      : ''}
                  </span>
                  <span className="text-blue-600 font-medium">
                    {b.cbd !== null && b.cbd !== undefined && Number(b.cbd) > 0
                      ? `CBD ${formatPercent(b.cbd)}${potencySuffix(b.test_units)}`
                      : ''}
                  </span>
                </div>
                <span className="text-sm font-semibold text-gray-900 tabular-nums w-16 text-right">
                  {formatCurrency(rowPrice)}
                </span>
              </div>

              {/* Per-batch qty stepper. Stops click bubbling so the row click
                  doesn't fight with the input clicks. */}
              <div
                className="flex items-center gap-1.5 shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => {
                    onSelect(b.lot_number)
                    onSetQty(b.lot_number, Math.max(0, qty - 1))
                  }}
                  disabled={qty <= 0}
                  className="p-1.5 bg-white border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Decrease"
                >
                  <Minus size={14} />
                </button>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={qty}
                  onFocus={() => onSelect(b.lot_number)}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/\D/g, '')
                    onSetQty(b.lot_number, cleaned === '' ? 0 : Number(cleaned))
                  }}
                  className="w-12 text-center text-sm font-semibold py-1 border border-gray-200 rounded-md tabular-nums focus:border-blue-400 focus:ring-0 outline-none"
                />
                <button
                  type="button"
                  onClick={() => {
                    onSelect(b.lot_number)
                    onSetQty(b.lot_number, Math.min(b.quantity, qty + 1))
                  }}
                  disabled={isMaxed}
                  className="p-1.5 bg-white border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Increase"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>
          </button>
        )
      })}
    </div>
  )
}

// ----- Discount panel: input + toggle on one row, height-stable quick row -----

interface DiscountPanelProps {
  /** "Your limit: N%"; null when unlimited or in refund mode. */
  limitLabel: string | null
  unitPrice: number
  discountMode: 'percent' | 'amount'
  setDiscountMode: (m: 'percent' | 'amount') => void
  discountInput: string
  setDiscountInput: (v: string) => void
  unitDiscountAmount: number
  unitDiscountPercent: number
  unitFinalPrice: number
  reasons: { id: number; name: string; percent?: number | null }[]
  reasonId: number | null
  setReasonId: (id: number | null) => void
}

function DiscountPanel({
  limitLabel,
  unitPrice,
  discountMode,
  setDiscountMode,
  discountInput,
  setDiscountInput,
  unitDiscountAmount,
  unitDiscountPercent,
  unitFinalPrice,
  reasons,
  reasonId,
  setReasonId,
}: DiscountPanelProps) {
  const hasDiscount = unitDiscountAmount > 0
  const quickValues = discountMode === 'percent' ? QUICK_PERCENTS : QUICK_DOLLARS
  const quickLabel = discountMode === 'percent' ? 'Quick %' : 'Quick $'
  const quickSuffix = discountMode === 'percent' ? '%' : ''
  const quickPrefix = discountMode === 'percent' ? '' : '$'

  // Cap the discount input at the unit price ($ mode) or 100 (% mode).
  // Without this, a cashier can type "$20" on a $19.03 item and the
  // displayed input disagrees with what's actually applied (the
  // computation already does Math.min with the price, but the typed
  // value persisted) — the cart line could then show a discount the
  // cashier didn't expect to be capped. Truncates to 2 decimals as well
  // so cents don't drift via the spinner. Empty / lone-dot pass through
  // so mid-typing doesn't fight the user.
  const discountMax = discountMode === 'percent' ? 100 : unitPrice
  const clampDiscountInput = (raw: string): string => {
    if (raw === '' || raw === '.') return raw
    // Trim to 2 decimals.
    const m = raw.match(/^\d*(\.\d{0,2})?/)
    const trimmed = m ? m[0] : ''
    if (!trimmed) return ''
    const parsed = parseFloat(trimmed)
    if (!Number.isFinite(parsed)) return trimmed
    if (parsed > discountMax) {
      // For dollar mode use the unit price's natural precision; for
      // percent we don't need decimals on a clamp to 100.
      return discountMode === 'percent' ? '100' : discountMax.toFixed(2)
    }
    return trimmed
  }

  return (
    <div className="space-y-4">
      {/* Per-unit current price (line totals live in the persistent footer) */}
      <div className="flex items-baseline justify-between">
        <span className="text-xs text-gray-500 uppercase tracking-wide">Price per unit</span>
        <span className="text-xl font-bold text-gray-900">{formatCurrency(unitPrice)}</span>
      </div>
      {limitLabel && <p className="text-xs text-gray-500 -mt-2">{limitLabel}</p>}

      {/* Input + %/$ segmented toggle on one row */}
      <div>
        <label className="text-xs text-gray-500 block mb-1">
          Discount {discountMode === 'percent' ? '%' : '$'} per unit
        </label>
        <div className="flex items-stretch gap-2">
          <div className="relative flex-1">
            <input
              type="number"
              inputMode="decimal"
              min={0}
              max={discountMax}
              step={discountMode === 'percent' ? 1 : 0.01}
              value={discountInput}
              onChange={(e) => setDiscountInput(clampDiscountInput(e.target.value))}
              placeholder="0"
              className="w-full text-2xl font-bold py-3 px-4 pr-10 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-0 outline-none tabular-nums"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg font-medium pointer-events-none">
              {discountMode === 'percent' ? '%' : '$'}
            </span>
          </div>
          {/* Segmented $/% toggle — $ is the default and lives on the left
              since cashiers usually type a dollar amount off a paper coupon
              before reaching for percent. */}
          <div className="inline-flex bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setDiscountMode('amount')}
              className={`w-12 rounded-lg flex items-center justify-center transition-colors cursor-pointer ${
                discountMode === 'amount'
                  ? 'bg-white text-green-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              aria-label="Dollar"
              title="Dollar"
            >
              <DollarSign size={18} />
            </button>
            <button
              onClick={() => setDiscountMode('percent')}
              className={`w-12 rounded-lg flex items-center justify-center transition-colors cursor-pointer ${
                discountMode === 'percent'
                  ? 'bg-white text-green-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              aria-label="Percent"
              title="Percent"
            >
              <Percent size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Quick presets — always rendered (stable height across modes) */}
      <div>
        <p className="text-xs text-gray-500 mb-1.5">{quickLabel}</p>
        <div className="grid grid-cols-4 gap-2">
          {quickValues.map((v) => {
            // Quick presets ($1/$5/$10/$20) can also exceed the unit
            // price on cheap items. Disable any preset above the cap so
            // the cashier sees they can't pick it. The "max it" preset
            // (`v === unitPrice`) is still clickable.
            const overCap = v > discountMax
            const isActive = parseFloat(discountInput) === v
            return (
              <button
                key={v}
                onClick={() => setDiscountInput(clampDiscountInput(String(v)))}
                disabled={overCap}
                className={`py-2 rounded-lg font-medium text-sm transition-colors border ${
                  overCap
                    ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed'
                    : isActive
                      ? 'bg-green-100 text-green-700 border-green-300 cursor-pointer'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 cursor-pointer'
                }`}
                title={overCap ? `Discount can't exceed unit price (${formatCurrency(unitPrice)})` : undefined}
              >
                {quickPrefix}{v}{quickSuffix}
              </button>
            )
          })}
        </div>
      </div>

      {/* Reason — required when discount > 0 */}
      <div>
        <label className="text-xs text-gray-500 block mb-1">
          Reason {hasDiscount && <span className="text-red-500">*</span>}
        </label>
        <select
          value={reasonId ?? ''}
          onChange={(e) => setReasonId(e.target.value ? Number(e.target.value) : null)}
          className={`w-full py-2.5 px-3 border-2 rounded-lg text-sm focus:ring-0 outline-none cursor-pointer ${
            hasDiscount && reasonId === null
              ? 'border-amber-400 focus:border-amber-500'
              : 'border-gray-200 focus:border-green-500'
          }`}
        >
          <option value="">Select a reason…</option>
          {reasons.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
              {r.percent ? ` (${r.percent}%)` : ''}
            </option>
          ))}
        </select>
        {hasDiscount && reasonId === null && (
          <p className="text-xs text-amber-600 mt-1">Required when applying a discount.</p>
        )}
      </div>

      {/* Live preview — per unit. Line totals are surfaced in the footer. */}
      {hasDiscount && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg space-y-1">
          <Row label="Subtotal / unit" value={formatCurrency(unitPrice)} />
          <Row
            label={`Discount / unit (${formatPercent(unitDiscountPercent)}%)`}
            value={`−${formatCurrency(unitDiscountAmount)}`}
            valueClass="text-green-700 font-semibold"
          />
          <div className="border-t border-green-200 pt-1 mt-1">
            <Row
              label="After discount / unit"
              value={formatCurrency(unitFinalPrice)}
              labelClass="font-semibold text-gray-900"
              valueClass="font-bold text-gray-900 text-base"
            />
          </div>
        </div>
      )}
    </div>
  )
}

function Row({
  label,
  value,
  labelClass = 'text-gray-600',
  valueClass = 'text-gray-900 tabular-nums',
}: {
  label: string
  value: string
  labelClass?: string
  valueClass?: string
}) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className={labelClass}>{label}</span>
      <span className={valueClass}>{value}</span>
    </div>
  )
}

interface NearbyPanelProps {
  unitPrice: number
  onSelectStore: (store: NearbyStore) => void
}

function NearbyPanel({ unitPrice, onSelectStore }: NearbyPanelProps) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const stores = useMemo(() => generateNearbyStores(unitPrice), [unitPrice])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return stores
    return stores.filter((s) => s.name.toLowerCase().includes(q))
  }, [stores, search])

  const totalPages = Math.max(1, Math.ceil(filtered.length / NEARBY_PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageItems = filtered.slice(
    (safePage - 1) * NEARBY_PAGE_SIZE,
    safePage * NEARBY_PAGE_SIZE,
  )

  useEffect(() => {
    setPage(1)
  }, [search])

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search stores…"
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-green-500 focus:ring-0 outline-none"
        />
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)_52px_72px_80px] gap-2 px-3 pt-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
        <span>Store</span>
        <span className="text-right">km</span>
        <span className="text-right">Price</span>
        <span className="text-right">Updated</span>
      </div>

      {pageItems.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-6">No stores match your search.</p>
      ) : (
        <div className="space-y-1">
          {pageItems.map((store) => {
            const canMatch = store.price < unitPrice
            const delta = +(unitPrice - store.price).toFixed(2)
            return (
              <button
                key={store.id}
                type="button"
                onClick={() => canMatch && onSelectStore(store)}
                disabled={!canMatch}
                title={
                  canMatch
                    ? `Apply ${formatCurrency(delta)} discount to match`
                    : 'Price is higher than current — no discount needed'
                }
                className={`w-full grid grid-cols-[minmax(0,1fr)_52px_72px_80px] gap-2 items-center px-3 py-2 rounded-lg text-sm border transition-colors ${
                  canMatch
                    ? 'bg-white border-gray-200 hover:border-green-300 hover:bg-green-50 cursor-pointer'
                    : 'bg-gray-50 border-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                <span className="truncate text-left font-medium">{store.name}</span>
                <span className="text-right tabular-nums">{store.distance.toFixed(1)}</span>
                <span
                  className={`text-right font-semibold tabular-nums ${
                    canMatch ? 'text-green-600' : ''
                  }`}
                >
                  {formatCurrency(store.price)}
                </span>
                <span className="text-right text-xs tabular-nums text-gray-500">
                  {formatRelativeTime(store.updatedAt)}
                </span>
              </button>
            )
          })}
        </div>
      )}

      {filtered.length > 0 && (
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <span className="text-xs text-gray-500 tabular-nums">
            {(safePage - 1) * NEARBY_PAGE_SIZE + 1}–
            {Math.min(safePage * NEARBY_PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="p-1.5 rounded-md border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Previous page"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="text-xs text-gray-600 px-2 tabular-nums">
              {safePage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="p-1.5 rounded-md border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Next page"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
